import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rpcTriagem } from '@/components/kanban/triagem/rpcTriagem';

export type FilaRitmo = 'instalacao' | 'instalacao_volta' | 'erro_solar' | 'input' | 'validacao' | 'credito';

export type PedidoRitmo = {
  pedido_id: string;
  numero_pedido: number | null;
  cliente: string | null;
  tipo_pessoa: string | null;
  batido: boolean;
  situacao: 'feito' | 'pendencia' | 'falta' | null;
  entrou_no_lugar: boolean;
  linhas: number;
  horas: number | null;
  tt: boolean;
  agenda: string | null;
};

export type ItemProximo = { pedido_id: string; numero_pedido: number | null; cliente: string | null };

export type Ritmo = {
  aplica: boolean;
  travado: boolean;
  ocupado?: boolean;
  expediente?: boolean;
  fila?: FilaRitmo | null;
  rodada?: number | null;
  travado_em?: string | null;
  limite_min?: number | null;
  decorrido_seg?: number | null;
  tem_teto?: boolean;
  pedidos_trava?: PedidoRitmo[];
  info?: { total: number; pedidos: (ItemProximo & { agenda: string })[] } | null;
  depois?: { input: number; validacao: number } | null;
  blocos_hoje?: number;
  travas_hoje?: number;
  erro_solar?: number;
  erro_solar_pendentes?: number;
  proximos?: {
    proxima_rodada: number | null;
    vencem: number;
    consulta: (ItemProximo & { fila: 'input' | 'validacao'; horas: number })[];
    credito: (ItemProximo & { defesa: string })[];
    instalacao: (ItemProximo & { agenda: string })[];
    instalacao_total: number;
  } | null;
};

export const MOTIVOS_PENDENCIA = [
  { codigo: 'solar_fora', rotulo: 'SOLAR fora do ar' },
  { codigo: 'pendencia_cliente', rotulo: 'Pendência do cliente' },
  { codigo: 'pendencia_consultor', rotulo: 'Pendência do consultor' },
  { codigo: 'aguardando_claro', rotulo: 'Aguardando a Claro' },
] as const;

/** O momento do dia da BKO Tramitação. Quem decide é fn_bko_tramitacao_ritmo. */
export function useRitmoTramitacao(perfilId: string | undefined, ativo: boolean) {
  const qc = useQueryClient();
  const chave = ['bko-tramitacao-ritmo', perfilId];
  return useQuery({
    queryKey: chave,
    enabled: ativo,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Ritmo> => {
      const { data, error } = await rpcTriagem('fn_bko_tramitacao_ritmo', {});
      if (error) throw new Error(error.message || 'Não deu para conferir o ritmo da Tramitação.');
      const r = data as Ritmo;
      // Outra consulta segurava o ciclo neste instante: mantém o que já estava na tela.
      if (r.ocupado) return qc.getQueryData<Ritmo>(chave) ?? r;
      return r;
    },
  });
}

export function usePendenciaExterna(onFeito: () => void) {
  return useMutation({
    mutationFn: async (b: { pedidoId: string; motivo: string; texto: string }) => {
      const { error } = await rpcTriagem('fn_bko_erro_solar_pendencia', { p_pedido: b.pedidoId, p_motivo: b.motivo, p_texto: b.texto });
      if (error) throw new Error(error.message || 'Não foi possível registrar a pendência.');
    },
    onSuccess: onFeito,
  });
}

/** Segundos que faltam no relógio, contando entre uma consulta e outra. */
export function useRestamSegundos(r: Ritmo | undefined, atualizadoEm: number) {
  const [agora, setAgora] = useState(() => Date.now());
  const conta = !!r?.travado && !!r.tem_teto && r.limite_min != null && r.decorrido_seg != null;
  useEffect(() => {
    if (!conta) return;
    const id = window.setInterval(() => setAgora(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [conta]);
  if (!conta || !r) return null;
  const passou = Math.max(0, (agora - atualizadoEm) / 1000);
  return Math.max(0, Math.round((r.limite_min ?? 0) * 60 - (r.decorrido_seg ?? 0) - passou));
}
